import { useEffect, useState } from 'react';
import { Mail, Printer, Phone, FileText, Loader2, Calendar, User } from 'lucide-react';
import { ForderungStagePill } from '@/components/forderung/ForderungStagePill';
import { supabase } from '@/lib/supabase';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TimelineItem {
  id: string;
  timestamp: string;
  type: 'mahn_event' | 'dokument' | 'email_draft';
  kanal: string | null;
  stage: number | null;
  label: string;
  frist_bis?: string | null;
  created_by_name?: string | null;
  email_draft_id?: string | null;
  dokument_id?: string | null;
}

interface MahnEvent {
  id: string;
  forderungsakte_id: string;
  stage: number;
  gesendet_am: string;
  kanal: string | null;
  dokument_id: string | null;
  email_draft_id: string | null;
  frist_bis: string | null;
  bemerkung: string | null;
}

interface DokumentRow {
  id: string;
  dateiname: string;
  kategorie: string | null;
  created_at: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatDateTime(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function KanalIcon({ kanal }: { kanal: string | null }) {
  const cls = 'h-4 w-4 flex-shrink-0';
  switch (kanal) {
    case 'email':
      return <Mail className={`${cls} text-blue-500`} />;
    case 'brief':
      return <Printer className={`${cls} text-gray-500`} />;
    case 'telefon':
      return <Phone className={`${cls} text-green-500`} />;
    default:
      return <FileText className={`${cls} text-muted-foreground`} />;
  }
}

function kanalLabel(kanal: string | null): string {
  switch (kanal) {
    case 'email':
      return 'E-Mail';
    case 'brief':
      return 'Brief';
    case 'telefon':
      return 'Telefonat';
    case 'intern':
      return 'Intern';
    default:
      return 'Aktion';
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface TimelineTabProps {
  forderungsAkteId: string;
}

export function TimelineTab({ forderungsAkteId }: TimelineTabProps) {
  const [items, setItems] = useState<TimelineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [mahnRes, dokRes] = await Promise.all([
          supabase
            .from('mahn_events')
            .select('id,forderungsakte_id,stage,gesendet_am,kanal,dokument_id,email_draft_id,frist_bis,bemerkung')
            .eq('forderungsakte_id', forderungsAkteId)
            .order('gesendet_am', { ascending: false }),
          supabase
            .from('dokumente')
            .select('id,dateiname,kategorie,created_at')
            .eq('forderungsakte_id', forderungsAkteId)
            .eq('kategorie', 'mahnung')
            .order('created_at', { ascending: false }),
        ]);

        if (cancelled) return;

        if (mahnRes.error) throw mahnRes.error;
        if (dokRes.error) throw dokRes.error;

        const mahnItems: TimelineItem[] = ((mahnRes.data ?? []) as MahnEvent[]).map((e) => ({
          id: `mahn-${e.id}`,
          timestamp: e.gesendet_am,
          type: 'mahn_event',
          kanal: e.kanal,
          stage: e.stage,
          label: kanalLabel(e.kanal),
          frist_bis: e.frist_bis,
          email_draft_id: e.email_draft_id,
          dokument_id: e.dokument_id,
        }));

        const dokItems: TimelineItem[] = ((dokRes.data ?? []) as DokumentRow[]).map((d) => ({
          id: `dok-${d.id}`,
          timestamp: d.created_at,
          type: 'dokument',
          kanal: 'brief',
          stage: null,
          label: d.dateiname || 'Dokument (Mahnung)',
          dokument_id: d.id,
        }));

        const merged = [...mahnItems, ...dokItems].sort(
          (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
        );

        setItems(merged);
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Fehler beim Laden der Timeline');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [forderungsAkteId]);

  if (loading) {
    return (
      <div
        data-testid="timeline-tab-content"
        className="flex items-center justify-center py-10 text-muted-foreground"
      >
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        <span className="text-sm">Lade Timeline…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="timeline-tab-content" className="p-4 text-sm text-red-600">
        {error}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div
        data-testid="timeline-tab-content"
        className="py-10 text-center text-sm text-muted-foreground"
      >
        Noch keine Aktivitäten vorhanden.
      </div>
    );
  }

  return (
    <div data-testid="timeline-tab-content" className="py-2">
      <ol className="relative border-l border-border ml-3">
        {items.map((item) => (
          <li
            key={item.id}
            data-testid="timeline-item"
            className="mb-6 ml-5"
          >
            {/* Dot on the timeline line */}
            <span className="absolute -left-[9px] flex h-4 w-4 items-center justify-center rounded-full border border-border bg-background ring-4 ring-background">
              <KanalIcon kanal={item.kanal} />
            </span>

            <div className="rounded-md border bg-card p-3 shadow-sm space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                {item.stage !== null && (
                  <ForderungStagePill stage={item.stage} />
                )}
                <span className="text-xs font-medium">{item.label}</span>
              </div>

              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {formatDateTime(item.timestamp)}
                </span>
                {item.frist_bis && (
                  <span className="flex items-center gap-1">
                    Frist: {formatDate(item.frist_bis)}
                  </span>
                )}
                {item.created_by_name && (
                  <span className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {item.created_by_name}
                  </span>
                )}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
